/**
 * ParlayCard Component
 * Single parlay recommendation display with integrated scoring
 * All scoring is now dynamic from database
 */

import ConfirmModal from '@components/ui/ConfirmModal';
import type { GameData } from '@components/ui/StatsBarChart';
import type { TeamGameData } from '@components/ui/TeamStatsBarChart';
import { TierBadge } from '@components/ui/TierBadge';
import UpgradeModal from '@components/ui/UpgradeModal';
import { useAuth } from '@contexts/AuthContext';
import { useToast } from '@contexts/ToastContext';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import type { ParlayScore } from '@hooks/useStrategyStats';
import type { GeneratedLeg, GeneratedParlay } from '@parlay-picker/shared/api/picksApi';
import { parlayLegToBuilderLeg } from '@parlay-picker/shared/builderLeg';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { isLlmProvider, type LlmProvider } from '@parlay-picker/shared/llmProviders';
import type { BestOddsResult } from '@parlay-picker/shared/oddsUtils';
import {
  hasParlayExpiredLegs,
  isLegExpired,
  isParlayFullyExpired,
} from '@parlay-picker/shared/pickExpiration';
import { parseReasoningWithRemovedLegs } from '@parlay-picker/shared/removedLegsUtils';
import { useParlayBuilderStore } from '@stores/parlayBuilderStore';
import { motion } from 'framer-motion';
import { ChevronDown, Loader2, Plus } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import type { RiskTier } from '../constants';
import { type Game, getConfidenceColor, type RiskTierGroup } from '../utils';
import { LegAnalysisDetails } from './LegAnalysisDetails';
import { LegCard } from './LegCard';
import { ScorePanel } from './ScorePanel';

const PROVIDER_BADGES: Record<LlmProvider, { label: string; className: string }> = {
  anthropic: { label: 'Claude', className: 'bg-purple-500/20 text-purple-400' },
  gemini: { label: 'Gemini', className: 'bg-theme-primary/20 text-theme-primary' },
  deepseek: { label: 'DeepSeek', className: 'bg-cyan-500/20 text-cyan-400' },
  openai: { label: 'OpenAI', className: 'bg-emerald-500/20 text-emerald-400' },
};

// =============================================================================
// Types
// =============================================================================

export interface ChartClickData {
  /** The league of the leg or search the chart was opened from. */
  leagueKey: LeagueKey;
  // Player props
  playerName?: string;
  playerTeamName?: string; // Player's team for logo in modal
  prop?: string;
  recentGames?: GameData[];
  h2hGames?: GameData[];
  propLine?: number | null;
  statKey?: string | null;
  opponentAbbr?: string;
  overUnder?: string | null;
  // Game props
  isGameProp?: boolean;
  teamName?: string;
  teamRecentGames?: TeamGameData[];
  teamH2hGames?: TeamGameData[];
  propType?: 'spread' | 'total' | 'moneyline';
  // Opponent team data (for switching perspective)
  opponentTeamName?: string;
  opponentTeamRecentGames?: TeamGameData[];
  // Phase 4: additional context
  odds?: string;
  opponentDefRank?: number;
  // Market key for period-specific charts
  marketKey?: string;
}

interface ParlayCardProps {
  rec: GeneratedParlay;
  index: number;
  tier: RiskTier | RiskTierGroup;
  games: Game[];
  isTracked: boolean;
  isTracking?: boolean;
  onTrack: (rec: GeneratedParlay, tierName: string) => void;
  onChartClick: (data: ChartClickData) => void;
  showDetailedAnalysis: boolean;
  /** Score from user's tracked parlays */
  dynamicScore?: ParlayScore | null;
  /** Score from all AI predictions */
  predictionScore?: ParlayScore | null;
  /** When true, shows unavailable state in score panel */
  scoresUnavailable?: boolean;
  /** Callback to refresh/retry scoring */
  onScoreRefresh?: () => void;
  /** When true, only shows header - legs and details are hidden */
  isCollapsed?: boolean;
  /** Callback to toggle collapse state */
  onToggleCollapse?: () => void;
  /** Odds comparison data for best-line badges */
  oddsComparisons?: Map<string, BestOddsResult>;
  /** Whether the AI Analysis detail section is expanded (controlled by parent for accordion) */
  isDetailExpanded?: boolean;
  /** Callback to toggle detail expansion (controlled by parent for accordion) */
  onToggleDetail?: () => void;
  /** When true, hides all action buttons (Track, Add to Builder) — gameId not available yet */
  isStreaming?: boolean;
  /** League key for legs in this parlay (used when adding to builder) */
  leagueKey: LeagueKey;
  /** Slate-relative quality rank in [0, 1]. Computed via percentileRank from the section level. */
  strengthPercentile?: number;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Card border from the personal score when there is one, else the AI score, which is all most
 * users have (backlog 240).
 */
function getCardBorderStyle(score: ParlayScore | null | undefined): string {
  if (score) {
    // Check for player warnings first
    if (score.playerWarnings.length > 0) {
      return 'ring-1 ring-orange-500/40';
    }
    switch (score.recommendation) {
      case 'bet':
        return 'ring-2 ring-theme-success/40';
      case 'skip':
        return 'ring-1 ring-theme-danger/30';
      default:
        return '';
    }
  }
  return '';
}

// =============================================================================
// Component
// =============================================================================

export function ParlayCard({
  rec,
  index,
  tier,
  games,
  isTracked,
  isTracking = false,
  onTrack,
  onChartClick,
  showDetailedAnalysis,
  dynamicScore,
  predictionScore,
  scoresUnavailable = false,
  onScoreRefresh,
  isCollapsed = false,
  onToggleCollapse,
  oddsComparisons,
  isDetailExpanded,
  onToggleDetail,
  isStreaming,
  leagueKey,
  strengthPercentile,
}: ParlayCardProps): React.ReactElement {
  // Use controlled state from parent (accordion) if provided, otherwise local state
  const [localExpanded, setLocalExpanded] = useState<boolean>(false);
  const expanded = onToggleDetail ? (isDetailExpanded ?? false) : localExpanded;
  const toggleExpanded = onToggleDetail ?? (() => setLocalExpanded((prev) => !prev));
  const [showScoringUpgrade, setShowScoringUpgrade] = useState(false);
  const { addAllLegs, getLegCount } = useParlayBuilderStore();
  const legCount = getLegCount();
  const { showToast } = useToast();
  const { user } = useAuth();
  const { isLocked } = useFeatureAccess();
  const scoringLocked = isLocked('scoring_breakdown');
  const deepAnalysisLocked = isLocked('scoring_deep_analysis');
  // Track which feature the upgrade modal targets
  const [scoringUpgradeFeature, setScoringUpgradeFeature] = useState<
    'scoring_breakdown' | 'scoring_deep_analysis'
  >('scoring_breakdown');

  // Determine card styling based on scores
  const cardBorderStyle = getCardBorderStyle(dynamicScore ?? predictionScore);

  // Error modal state for build failures
  const [buildErrorMessage, setBuildErrorMessage] = useState<string | null>(null);
  const [building, setBuilding] = useState(false);
  const allExpired = isParlayFullyExpired(rec);

  // Handle adding all legs to builder (pre-filters expired legs)
  const handleAddToBuilder = (): void => {
    if (!rec.legs?.length) return;
    setBuilding(true);

    const expiredCount = rec.legs.filter((leg) => isLegExpired(leg)).length;
    const addableLegs = rec.legs.filter((leg) => !isLegExpired(leg));

    if (addableLegs.length === 0) {
      setBuilding(false);
      setBuildErrorMessage(
        `All ${expiredCount} leg(s) have expired — games have already started. Generate new picks for upcoming games.`,
      );
      return;
    }

    const builderLegs = addableLegs.flatMap((leg) => {
      const builderLeg = parlayLegToBuilderLeg(leg, rec, leagueKey);
      return builderLeg ? [builderLeg] : [];
    });
    if (builderLegs.length === 0) {
      setBuilding(false);
      setBuildErrorMessage('This parlay names no sportsbook, so its legs cannot be added.');
      return;
    }
    const result = addAllLegs(builderLegs);

    // Show feedback to user
    if (result.addedCount === 0 && result.blockedCount > 0) {
      setBuildErrorMessage(`Could not add legs: ${result.blockedReasons.join(', ')}`);
    } else if (result.blockedCount > 0 || expiredCount > 0) {
      const parts: string[] = [];
      if (result.blockedCount > 0)
        parts.push(`${result.blockedCount} blocked: ${result.blockedReasons.join(', ')}`);
      if (expiredCount > 0) parts.push(`${expiredCount} expired`);
      showToast(`Added ${result.addedCount} leg(s). ${parts.join('. ')}`, 'warning');
    } else if (result.addedCount > 0) {
      showToast(`Added ${result.addedCount} leg(s) to builder`, 'success');
    }
    setBuilding(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 } as const}
      animate={{ opacity: 1, y: 0 } as const}
      transition={{ duration: 0.35, delay: index * 0.08 }}
      whileHover={{ scale: 1.01 }}
      data-testid="picks-parlay-card"
      className={`bg-theme-surface rounded-xl p-4 rebrand-parlay-card ${cardBorderStyle}`}
    >
      {/* Header with rank, confidence, and odds - clickable to collapse */}
      <div
        className={`flex justify-between items-start ${onToggleCollapse ? 'cursor-pointer' : ''} ${isCollapsed ? '' : 'mb-3'}`}
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-theme-primary/15 flex items-center justify-center font-bold text-lg text-theme-primary">
            {rec.rank || index + 1}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-bold text-theme-text">#{rec.rank || index + 1} Pick</h4>
              {rec.isOptimized && (
                <span
                  className="text-[10px] bg-purple-600/80 px-1.5 py-0.5 rounded text-white font-medium"
                  title="Built by parlay optimizer"
                >
                  Optimized
                </span>
              )}
              {typeof strengthPercentile === 'number' && (
                <span
                  title="Quality rank within this generated slate, based on mean scoring model score"
                  className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                    strengthPercentile >= 0.9
                      ? 'bg-emerald-500/20 text-emerald-300'
                      : strengthPercentile >= 0.7
                        ? 'bg-sky-500/20 text-sky-300'
                        : strengthPercentile >= 0.3
                          ? 'bg-theme-surface-hover text-theme-text-secondary'
                          : 'bg-theme-surface-hover text-theme-text-tertiary'
                  }`}
                >
                  Top {Math.max(1, Math.round((1 - strengthPercentile) * 100))}%
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-sm ${getConfidenceColor(rec.confidence)}`}>
                {rec.confidence} Confidence
              </span>
              {/* Admin only: Show score reliability badges and provider */}
              {user?.role === 'admin' && (
                <>
                  {dynamicScore?.scoreReliability !== undefined && (
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${
                        dynamicScore.scoreReliability >= 76
                          ? 'bg-theme-success/20 text-theme-success'
                          : dynamicScore.scoreReliability >= 51
                            ? 'bg-theme-warning/20 text-theme-warning'
                            : dynamicScore.scoreReliability >= 26
                              ? 'bg-orange-500/20 text-orange-400'
                              : 'bg-theme-danger/20 text-theme-danger'
                      }`}
                      title="User score data reliability"
                    >
                      User: {dynamicScore.scoreReliability}%
                    </span>
                  )}
                  {predictionScore?.scoreReliability !== undefined && (
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${
                        predictionScore.scoreReliability >= 76
                          ? 'bg-theme-success/20 text-theme-success'
                          : predictionScore.scoreReliability >= 51
                            ? 'bg-theme-warning/20 text-theme-warning'
                            : predictionScore.scoreReliability >= 26
                              ? 'bg-orange-500/20 text-orange-400'
                              : 'bg-theme-danger/20 text-theme-danger'
                      }`}
                      title="AI score data reliability"
                    >
                      AI: {predictionScore.scoreReliability}%
                    </span>
                  )}
                  {/* LLM Provider badge for A/B testing */}
                  {rec.provider && (
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${
                        isLlmProvider(rec.provider)
                          ? PROVIDER_BADGES[rec.provider].className
                          : 'bg-theme-surface-hover/20 text-theme-text-secondary'
                      }`}
                      title={`Generated by ${rec.provider}`}
                    >
                      {isLlmProvider(rec.provider)
                        ? PROVIDER_BADGES[rec.provider].label
                        : rec.provider}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-xl font-bold text-theme-success">{rec.totalOdds || rec.odds}</div>
            <div className="text-theme-text-secondary text-sm">{rec.legs?.length || 0} legs</div>
          </div>
          {/* Collapse/expand indicator */}
          {onToggleCollapse && (
            <ChevronDown
              data-testid="parlay-expand-toggle"
              className={`w-5 h-5 text-theme-text-secondary transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
            />
          )}
        </div>
      </div>

      {/* Collapsible content */}
      {!isCollapsed && (
        <>
          {/* Score Panel - Dynamic DB scoring */}
          <ScorePanel
            dynamicScore={dynamicScore ?? null}
            predictionScore={predictionScore ?? null}
            scoresUnavailable={scoresUnavailable}
            onRefresh={onScoreRefresh}
            className="mb-3"
            legs={rec.legs}
          />

          {/* Legs */}
          <div className="divide-y divide-dashed divide-theme-border/30 mb-3">
            {rec.legs?.map((leg: GeneratedLeg, j: number) => (
              <div key={j} className="py-2 first:pt-0 last:pb-0">
                <LegCard
                  leg={leg}
                  games={games}
                  onChartClick={onChartClick}
                  parlay={rec}
                  enableSelection
                  oddsComparisons={oddsComparisons}
                  isStreaming={isStreaming}
                  leagueKey={leagueKey}
                />
              </div>
            ))}
          </div>

          {/* Reasoning */}
          <div className="text-theme-text-secondary text-sm border-t border-theme-border pt-3">
            <p>{parseReasoningWithRemovedLegs(rec.reasoning).mainReasoning}</p>
            {rec.removedLegs && rec.removedLegs.length > 0 && (
              <p className="text-yellow-500 text-xs mt-1">
                Note:{' '}
                {rec.removedLegs
                  .map(
                    (l: { player: string; prop: string; reason: string }) =>
                      `${l.player} ${l.prop} — ${l.reason}`,
                  )
                  .join('; ')}
              </p>
            )}
            <p className="text-theme-text-tertiary text-xs mt-2 italic">
              AI-generated. May contain errors.
            </p>
          </div>

          {/* Expired banner */}
          {isParlayFullyExpired(rec) && (
            <div className="flex items-center justify-center gap-2 bg-red-500/90 text-white text-xs font-bold rounded-lg py-1.5 mt-3">
              Picks Expired — Games Have Started
            </div>
          )}

          {/* Action Buttons — hidden during streaming (gameId not set until complete event) */}
          {!isStreaming && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-theme-border/30">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onTrack(rec, tier.name);
                }}
                disabled={isTracked || isTracking || hasParlayExpiredLegs(rec)}
                className={`flex-1 py-2 font-medium text-sm transition flex items-center justify-center gap-2 rounded-xl ${
                  isTracked || isTracking || hasParlayExpiredLegs(rec)
                    ? 'bg-theme-bg text-theme-text-tertiary cursor-not-allowed'
                    : 'bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill shadow-lg shadow-theme-primary/20'
                }`}
              >
                {isTracking && <Loader2 className="w-4 h-4 animate-spin" />}
                {isTracking
                  ? 'Tracking...'
                  : isTracked
                    ? 'Tracked'
                    : hasParlayExpiredLegs(rec)
                      ? 'Game Started'
                      : 'Track Parlay'}
              </button>
              {!allExpired && (
                <button
                  type="button"
                  disabled={building}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToBuilder();
                  }}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition flex items-center gap-1 ${
                    building
                      ? 'bg-theme-bg text-theme-text-tertiary cursor-not-allowed'
                      : 'bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill'
                  }`}
                  title="Add all legs to the parlay builder"
                >
                  {building ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  {building ? 'Adding...' : legCount > 0 ? 'Add to Builder' : 'Build'}
                </button>
              )}
            </div>
          )}

          {/* Expandable AI Analysis Details Section */}
          {showDetailedAnalysis &&
            (scoringLocked ? (
              /* Free: show PRO badge */
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setScoringUpgradeFeature('scoring_breakdown');
                  setShowScoringUpgrade(true);
                }}
                className="w-full mt-3 flex items-center justify-center gap-2 text-sm text-theme-text-tertiary hover:text-theme-text-secondary transition"
              >
                <span>AI Analysis</span>
                <TierBadge feature="scoring_breakdown" size="md" />
              </button>
            ) : deepAnalysisLocked ? (
              /* Pro: show PREMIUM badge */
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setScoringUpgradeFeature('scoring_deep_analysis');
                  setShowScoringUpgrade(true);
                }}
                className="w-full mt-3 flex items-center justify-center gap-2 text-sm text-theme-text-tertiary hover:text-theme-text-secondary transition"
              >
                <span>AI Analysis</span>
                <TierBadge feature="scoring_deep_analysis" size="md" />
              </button>
            ) : (
              /* Premium: full expand/collapse */
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpanded();
                  }}
                  className="w-full mt-3 flex items-center justify-center gap-2 text-sm text-theme-primary hover:text-theme-primary-hover transition"
                >
                  <span>{expanded ? 'Hide AI Analysis' : 'Show AI Analysis'}</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
                  />
                </button>

                {expanded && (
                  <div className="mt-3 pt-3 border-t border-theme-border space-y-3">
                    <h5 className="text-xs font-semibold text-theme-text-tertiary uppercase tracking-wider">
                      Leg-by-Leg AI Breakdown
                    </h5>
                    {rec.legs?.map((leg: GeneratedLeg, j: number) => (
                      <LegAnalysisDetails key={j} leg={leg} />
                    ))}
                  </div>
                )}
              </>
            ))}
        </>
      )}

      {showScoringUpgrade && (
        <UpgradeModal
          mode="feature"
          feature={scoringUpgradeFeature}
          onDismiss={() => setShowScoringUpgrade(false)}
        />
      )}

      {/* Error modal for build failures */}
      <ConfirmModal
        isOpen={buildErrorMessage !== null}
        title="Cannot Build Parlay"
        message={buildErrorMessage ?? ''}
        confirmLabel="OK"
        onConfirm={() => setBuildErrorMessage(null)}
        onCancel={() => setBuildErrorMessage(null)}
      />
    </motion.div>
  );
}
